/**
 * Order matching engine for the prediction market simulation.
 * Implements price-time priority matching with support for limit and market orders.
 */

import type {
  Order,
  NewOrder,
  OrderBook,
  Trade,
  MatchResult,
  MarketState,
  OrderValidationContext,
} from './types'
import { validateOrder } from './validation'

/**
 * Generates a random ID for orders and trades.
 * Uses base-36 encoding for compact alphanumeric IDs.
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 15)
}

/**
 * The matching engine processes orders against an order book.
 * Implements price-time priority matching with support for limit and market orders.
 */
export class MatchingEngine {
  /**
   * Process an incoming order against the order book.
   *
   * Limit orders:
   * - Buy: Match against asks with price <= order price
   * - Sell: Match against bids with price >= order price
   * - Unmatched quantity rests on the book
   *
   * Market orders:
   * - Execute immediately against best available price
   * - Reject if insufficient liquidity
   *
   * Price-time priority: Orders at same price level are matched in FIFO order.
   *
   * @param order - The new order to process
   * @param book - The current order book (will be mutated)
   * @param context - User's available trading resources for validation
   * @returns Result containing trades, updated order, and whether order was added to book
   */
  processOrder(order: NewOrder, book: OrderBook, context: OrderValidationContext): MatchResult {
    // 1. Validate order
    const validation = validateOrder(order, context)
    if (!validation.isValid) {
      return {
        trades: [],
        updatedOrder: {
          ...order,
          id: generateId(),
          timestamp: Date.now(),
          status: 'rejected',
          filledQuantity: 0,
          rejectionReason: validation.rejectionReason,
        },
        addedToBook: false,
        cashDelta: 0,
      }
    }

    // 2. Create order with ID and timestamp
    const incomingOrder: Order = {
      ...order,
      id: generateId(),
      timestamp: Date.now(),
      status: 'open',
      filledQuantity: 0,
    }

    // 3. Match against opposite side
    const trades: Trade[] = []
    let cashDelta = 0
    const oppositeOrders = order.side === 'buy'
      ? [...book.asks]  // Already sorted by price ascending
      : [...book.bids]  // Already sorted by price descending

    for (const restingOrder of oppositeOrders) {
      // Check if prices cross
      const pricesCross = order.side === 'buy'
        ? (order.type === 'market' || restingOrder.price <= order.price)
        : (order.type === 'market' || restingOrder.price >= order.price)

      if (!pricesCross) break

      // Calculate trade quantity
      const availableQty = restingOrder.quantity - restingOrder.filledQuantity
      const neededQty = incomingOrder.quantity - incomingOrder.filledQuantity
      const tradeQty = Math.min(availableQty, neededQty)

      if (tradeQty > 0) {
        // Create trade at resting order's price (price improvement for taker)
        const trade: Trade = {
          id: generateId(),
          price: restingOrder.price,
          quantity: tradeQty,
          timestamp: Date.now(),
          buyOrderId: order.side === 'buy' ? incomingOrder.id : restingOrder.id,
          sellOrderId: order.side === 'sell' ? incomingOrder.id : restingOrder.id,
          takerSide: order.side,
        }
        trades.push(trade)

        // Track signed cash movement for the incoming order.
        if (order.side === 'buy') {
          cashDelta -= tradeQty * restingOrder.price
        } else {
          cashDelta += tradeQty * restingOrder.price
        }

        // Update filled quantities
        incomingOrder.filledQuantity += tradeQty
        restingOrder.filledQuantity += tradeQty

        // Update resting order status
        if (restingOrder.filledQuantity === restingOrder.quantity) {
          restingOrder.status = 'filled'
        } else {
          restingOrder.status = 'partial'
        }
      }

      // Stop if incoming order is fully filled
      if (incomingOrder.filledQuantity === incomingOrder.quantity) {
        incomingOrder.status = 'filled'
        break
      }
    }

    // 4. Handle remaining quantity
    let addedToBook = false
    if (incomingOrder.filledQuantity < incomingOrder.quantity) {
      if (order.type === 'limit') {
        // Add remaining to book
        incomingOrder.status = incomingOrder.filledQuantity > 0 ? 'partial' : 'open'
        if (order.side === 'buy') {
          book.bids.push(incomingOrder)
          book.bids.sort((a, b) => b.price - a.price)
        } else {
          book.asks.push(incomingOrder)
          book.asks.sort((a, b) => a.price - b.price)
        }
        addedToBook = true
      } else {
        // Market order with insufficient liquidity
        if (incomingOrder.filledQuantity === 0) {
          incomingOrder.status = 'rejected'
          incomingOrder.rejectionReason = 'no-liquidity'
        } else {
          incomingOrder.status = 'partial'
        }
      }
    }

    // 5. Update order book (remove filled orders)
    book.bids = book.bids.filter(o => o.status !== 'filled')
    book.asks = book.asks.filter(o => o.status !== 'filled')

    // 6. Update last price and trade history
    if (trades.length > 0) {
      book.lastPrice = trades[trades.length - 1].price
      book.trades.push(...trades)
    }

    return { trades, updatedOrder: incomingOrder, addedToBook, cashDelta }
  }

  /**
   * Cancel an open order.
   * Returns a new OrderBook with the order removed (immutable operation).
   *
   * @param orderId - ID of the order to cancel
   * @param book - The current order book
   * @returns Object with success flag and updated book
   */
  cancelOrder(orderId: string, book: OrderBook): { success: boolean; book: OrderBook } {
    // Search bids first
    const bidIndex = book.bids.findIndex(
      o => o.id === orderId && (o.status === 'open' || o.status === 'partial')
    )
    if (bidIndex !== -1) {
      return {
        success: true,
        book: {
          ...book,
          bids: book.bids.filter((_, i) => i !== bidIndex),
        },
      }
    }

    // Search asks if not found in bids
    const askIndex = book.asks.findIndex(
      o => o.id === orderId && (o.status === 'open' || o.status === 'partial')
    )
    if (askIndex !== -1) {
      return {
        success: true,
        book: {
          ...book,
          asks: book.asks.filter((_, i) => i !== askIndex),
        },
      }
    }

    // Order not found
    return { success: false, book }
  }

  /**
   * Calculate current market state from the order book.
   *
   * @param book - The order book to analyze
   * @returns Market state with best bid/ask, spread, and implied probability
   */
  getMarketState(book: OrderBook): MarketState {
    const bestBid = book.bids.length > 0 ? book.bids[0].price : null
    const bestAsk = book.asks.length > 0 ? book.asks[0].price : null
    const spread = bestBid !== null && bestAsk !== null ? bestAsk - bestBid : null
    const midPrice = bestBid !== null && bestAsk !== null ? (bestBid + bestAsk) / 2 : null
    const impliedProbability = midPrice ?? book.lastPrice ?? null

    return { bestBid, bestAsk, spread, midPrice, impliedProbability }
  }
}
